import { BlockBlobClient } from '@azure/storage-blob';
import { GoFormzServices } from '../../AppSettings';
import { apimCall, publicShareApimCall } from '../../helpers/api';
import { PublicFormPersistedState, PublicFormPersistedStateDispatch, PublicFormPersistedStateUpdate } from './publicformpersistedstate-types';
import publicStateDefaults from '../publicforms/publicformpersistedstate-defaults';
import moment from 'moment';
import { RootState } from 'gf-types';

export const GET_PUBLIC_FORM_PERSISTED_STATE_SUCCESS = 'GET_PUBLIC_FORMPERSISTED_STATE_SUCCESS';
export const GET_PUBLIC_FORM_PERSISTED_STATE_FAILURE = 'GET_PUBLIC_FORMPERSISTED_STATE_FAILURE';
export const SET_PUBLIC_FORM_PERSISTED_STATE_SUCCESS = 'SET_PUBLIC_FORMPERSISTED_STATE_SUCCESS';
export const GET_PUBLIC_FORM_PERSISTED_STATE_BLOB_NOT_FOUND_FAILURE = 'GET_PUBLIC_FORMPERSISTED_STATE_BLOB_NOT_FOUND_FAILURE';
export const GET_PUBLIC_FORM_PERSISTED_STATE_LOADING = 'GET_PUBLIC_FORM_PERSISTED_STATE_LOADING';
const browserStorageKey = 'clientid:publicformpersistedstate';

const addStorageListener = (dispatch: PublicFormPersistedStateDispatch) => {
  window.addEventListener('storage', (ev: StorageEvent) => {
    if (ev.key !== browserStorageKey) {
      return;
    }

    const stateFromStorage = JSON.parse(localStorage.getItem(browserStorageKey) ?? '{}');
    dispatch({
      type: GET_PUBLIC_FORM_PERSISTED_STATE_SUCCESS,
      payload: stateFromStorage
    });
  });
};

export function getPublicFormPersistedState(isPublicForm: boolean, sharedAccessToken?: string) {
  return async function (dispatch: PublicFormPersistedStateDispatch) {
    dispatch({ type: GET_PUBLIC_FORM_PERSISTED_STATE_LOADING, payload: { loading: true } });

    try {
      const blobUrl = isPublicForm
        ? await publicShareApimCall('/session/publicformpersistedstate', 'GET', null, null, sharedAccessToken)
        : await apimCall(GoFormzServices.AccountService, `/profile/publicformpersistedstate`, 'GET');
      const blobClient = new BlockBlobClient(blobUrl.data);

      const json = await blobClient.download();
      const jsonBlob = await json.blobBody;
      const jsonText = await jsonBlob.text();
      const state = JSON.parse(jsonText) as PublicFormPersistedState;

      const stateWithDefaults = { ...publicStateDefaults.configs, ...state.configs };
      if (!sharedAccessToken) {
        addStorageListener(dispatch);
      }

      dispatch({
        type: GET_PUBLIC_FORM_PERSISTED_STATE_SUCCESS,
        payload: { configs: stateWithDefaults, loading: false }
      });
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 404) {
        dispatch({ type: GET_PUBLIC_FORM_PERSISTED_STATE_BLOB_NOT_FOUND_FAILURE, payload: { configs: publicStateDefaults.configs, loading: false } });
      } else {
        dispatch({ type: GET_PUBLIC_FORM_PERSISTED_STATE_FAILURE, payload: { loading: false } });
      }
    }
  };
}

export function setPublicFormPersistedState(update: PublicFormPersistedStateUpdate) {
  return async function (dispatch: PublicFormPersistedStateDispatch, getState: () => RootState): Promise<void> {
    try {
      // upload whitelabel logo file to blob storage for emails
      const currentLogo = update?.configs?.logoImage ?? '';
      const whitelabelLogoBlobName = await uploadLogoToWhitelabelStorage(currentLogo);

      const currentState = getState()?.publicFormPersistedState ?? {};
      const fullUpdate = {
        ...currentState,
        ...update,
        configs: {
          ...currentState.configs,
          ...update?.configs,
          ...{ logoUrl: whitelabelLogoBlobName }
        }
      };
      const stateJson = JSON.stringify(fullUpdate ?? {});
      const stateJsonBlobLength = new Blob([stateJson]).size;

      //update browser storage to support cross tabs
      localStorage.setItem(browserStorageKey, stateJson);

      dispatch({
        type: SET_PUBLIC_FORM_PERSISTED_STATE_SUCCESS,
        payload: fullUpdate
      });

      //upload to server for any new requests
      await getPublicFormPersistedStateBlobClient()
        .then((persistedStateBlobClient) => persistedStateBlobClient.upload(stateJson, stateJsonBlobLength))
        .catch((e) => console.log(e));
    } catch (err) {
      console.error(err);
      throw err;
    }
  };
}

const PublicFormPersistedBlobState = {
  blobUrl: null
};

const getPublicFormPersistedStateBlobClient = async (refreshSasToken?: boolean) => {
  let sasExpired = false;
  if (PublicFormPersistedBlobState.blobUrl != null) {
    const decodedUri = decodeURIComponent(PublicFormPersistedBlobState.blobUrl);
    const urlParams = new URLSearchParams(decodedUri);
    const expirationDate = urlParams.get('se');
    const currentTimeUtc = moment().utc().toISOString();

    if (currentTimeUtc > expirationDate) {
      sasExpired = true;
    }
  }

  if (PublicFormPersistedBlobState.blobUrl == null || refreshSasToken || sasExpired) {
    const blobUrlResponse = await apimCall(GoFormzServices.AccountService, `/profile/publicformpersistedstate`, 'GET');
    if (blobUrlResponse.status != 200) {
      console.error('Failed To Get PublicFormPersistedState BlobUrl', blobUrlResponse);
      return null;
    }
    PublicFormPersistedBlobState.blobUrl = blobUrlResponse.data;
  }

  const blobClient = new BlockBlobClient(PublicFormPersistedBlobState.blobUrl);
  return blobClient;
};

const uploadLogoToWhitelabelStorage = async (logoImage: string) => {
  if (!logoImage) return null;

  const mimeTypeMatch = logoImage.match(/^data:([^;]+);base64,/);
  const contentType = mimeTypeMatch ? mimeTypeMatch[1] : 'image/png';

  const base64Data = logoImage.split(',')[1];
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const logoBlob = new Blob([bytes], { type: contentType });
  const logoBlobLength = logoBlob.size;

  try {
    const whitelabelBlobClient = await getWhitelabelFilesBlobClient();
    if (!whitelabelBlobClient) return null;

    await whitelabelBlobClient.upload(logoBlob, logoBlobLength, { blobHTTPHeaders: { blobContentType: contentType } });
    return whitelabelBlobClient.name;
  } catch (e) {
    console.log(e);
    return null;
  }
};

const getWhitelabelFilesBlobClient = async () => {
  const blobUrlResponse = await apimCall(GoFormzServices.PublicFormsService, `/whitelabel/files/sas`, 'GET');
  if (blobUrlResponse.status != 200) {
    console.error('Failed To Get WhitelabelFiles BlobUrl', blobUrlResponse);
    return null;
  }

  const blobClient = new BlockBlobClient(blobUrlResponse.data);
  return blobClient;
};
